/**
 * KAN-826 (stage 2): helpers of the "Организация" and "Свои поля" sections of
 * the list upload form.
 *
 * - autoSelectCompanyFields: pick a file column for each organization field
 *   (saved preset column first, then exact label, then a "contains" term);
 * - validateExtraFields: client-side mirror of the backend rules for custom
 *   fields (api/services/dataset_upload_fields.py), messages per row;
 * - toExtraFieldsPayload / cleanCompanyFields: what goes to the upload;
 * - extractSectionErrors: messages of a 400 under one key.
 */
import type {
  ColumnMapping,
  CompanyFieldKey,
  CompanyFieldsMapping,
  ExtraFieldMapping,
  ExtraFieldTarget,
  SchemaField,
} from '@/types/dataset';

export const MAX_EXTRA_FIELDS = 15;
export const MAX_EXTRA_NAME_CHARS = 40;

export interface CompanyFieldDef {
  key: CompanyFieldKey;
  label: string;
}

/** Organization fields in the order of the form (labels as on the backend). */
export const COMPANY_FIELDS: ReadonlyArray<CompanyFieldDef> = [
  { key: 'inn', label: 'ИНН' },
  { key: 'ogrn', label: 'ОГРН' },
  { key: 'address', label: 'Адрес' },
  { key: 'city', label: 'Город' },
  { key: 'region', label: 'Регион' },
  { key: 'phones', label: 'Телефон организации' },
  { key: 'emails', label: 'Почта' },
  { key: 'website', label: 'Сайт' },
];

/**
 * Header terms per field, in the order fields are matched: the more specific
 * terms first, so "Адрес электронной почты" goes to the mail, not the address.
 */
const MATCH_ORDER: ReadonlyArray<[CompanyFieldKey, string[]]> = [
  ['inn', ['инн']],
  ['ogrn', ['огрн']],
  ['emails', ['почт', 'email', 'e-mail', 'электронн']],
  ['website', ['сайт', 'site']],
  ['phones', ['телефон']],
  ['city', ['город']],
  ['region', ['регион']],
  ['address', ['адрес']],
];

export const TARGET_OPTIONS: ReadonlyArray<{ value: ExtraFieldTarget; label: string }> = [
  { value: 'company', label: 'Организация' },
  { value: 'contact', label: 'Контакт' },
];

/** A custom field row of the form; `id` is local to the UI. */
export interface ExtraFieldDraft extends ExtraFieldMapping {
  id: string;
}

const norm = (value: string | undefined | null): string => (value ?? '').trim().toLowerCase();

/** Name as the backend stores it: trimmed, inner spaces collapsed. */
export const normalizeExtraName = (name: string): string => name.split(/\s+/).filter(Boolean).join(' ');

/**
 * Column for each organization field among `headers`; '' when nothing fits.
 * A column feeds one organization field at most. The phone of the organization
 * never takes a column already mapped to a contact field.
 */
export function autoSelectCompanyFields(
  headers: string[],
  saved: CompanyFieldsMapping,
  contactMapping: ColumnMapping
): Record<CompanyFieldKey, string> {
  const result = Object.fromEntries(COMPANY_FIELDS.map((f) => [f.key, ''])) as Record<
    CompanyFieldKey,
    string
  >;
  const used = new Set<string>();
  const contactColumns = new Set(Object.values(contactMapping).filter(Boolean));
  const take = (key: CompanyFieldKey, column: string | undefined) => {
    if (!column) return;
    result[key] = column;
    used.add(column);
  };
  const free = (key: CompanyFieldKey, header: string) =>
    !used.has(header) && !(key === 'phones' && contactColumns.has(header));

  COMPANY_FIELDS.forEach(({ key }) => {
    const column = saved[key];
    if (column && headers.includes(column)) take(key, column);
  });
  COMPANY_FIELDS.forEach(({ key, label }) => {
    if (result[key]) return;
    take(key, headers.find((h) => free(key, h) && norm(h) === norm(label)));
  });
  MATCH_ORDER.forEach(([key, terms]) => {
    if (result[key]) return;
    take(key, headers.find((h) => free(key, h) && terms.some((term) => norm(h).includes(term))));
  });
  return result;
}

/** Only the mapped organization fields, columns trimmed. */
export function cleanCompanyFields(fields: CompanyFieldsMapping): CompanyFieldsMapping {
  const result: CompanyFieldsMapping = {};
  (Object.entries(fields) as [CompanyFieldKey, string | undefined][]).forEach(([key, column]) => {
    const value = column?.trim();
    if (value) result[key] = value;
  });
  return result;
}

function reservedNames(schema: SchemaField[]): Set<string> {
  const names = [
    ...COMPANY_FIELDS.flatMap((f) => [f.key, f.label]),
    ...schema.flatMap((f) => [f.name, f.label]),
  ];
  return new Set(names.map(norm).filter(Boolean));
}

/** `{rowId: message}` for invalid custom fields; {} when all are valid. */
export function validateExtraFields(
  rows: ExtraFieldDraft[],
  schema: SchemaField[]
): Record<string, string> {
  const reserved = reservedNames(schema);
  const seen = new Set<string>();
  const errors: Record<string, string> = {};
  rows.forEach((row, index) => {
    const name = normalizeExtraName(row.name);
    const key = name.toLowerCase();
    let message = '';
    if (index >= MAX_EXTRA_FIELDS) message = `Своих полей не больше ${MAX_EXTRA_FIELDS}.`;
    else if (!name) message = 'Укажите название поля.';
    else if (name.length > MAX_EXTRA_NAME_CHARS)
      message = `Название не длиннее ${MAX_EXTRA_NAME_CHARS} символов.`;
    else if (reserved.has(key)) message = 'Название совпадает со стандартным полем.';
    else if (seen.has(key)) message = 'Название повторяется.';
    else if (!row.column.trim()) message = 'Выберите колонку.';
    if (name) seen.add(key);
    if (message) errors[row.id] = message;
  });
  return errors;
}

/** Custom fields as the upload sends them. */
export function toExtraFieldsPayload(rows: ExtraFieldDraft[]): ExtraFieldMapping[] {
  return rows.map(({ name, column, target, to_bot }) => ({
    name: normalizeExtraName(name),
    column: column.trim(),
    target,
    to_bot,
  }));
}

/** Messages of a 400 under `key` (a list or a single string), else []. */
export function extractSectionErrors(err: unknown, key: string): string[] {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data;
  if (!data || typeof data !== 'object') return [];
  const raw = (data as Record<string, unknown>)[key];
  const list = Array.isArray(raw) ? raw : [raw];
  return list.filter((item): item is string => typeof item === 'string' && !!item);
}
